import { useState, useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, PlusCircle,
  ShoppingCart, User, LogOut, Shield,
  Search, MessageCircle, Bell, CheckCheck, X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import api from "../../utils/api";
import "./Sidebar.css";

const navLinks = [
  { to: "/app/dashboard",   icon: LayoutDashboard, label: "Dashboard"    },
  { to: "/app/marketplace", icon: ShoppingBag,      label: "Marketplace"  },
  { to: "/app/lostfound",   icon: Search,            label: "Lost & Found" },
  { to: "/app/chat",        icon: MessageCircle,     label: "Messages"     },
  { to: "/app/cart",        icon: ShoppingCart,      label: "My Cart"      },
  { to: "/app/profile",     icon: User,              label: "Profile"      },
];

export default function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const { unreadChats, newNotif } = useSocket();

  const [notifs,      setNotifs]      = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen,   setNotifOpen]   = useState(false);

  const popupRef  = useRef(null);
  const bellRef   = useRef(null);

  // ── Fetch notifications ───────────────────────────────────
  const fetchNotifs = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/users/notifications");
      setNotifs(data.notifications.slice(0, 8));
      setUnreadCount(data.notifications.filter((n) => !n.read).length);
    } catch {}
  };

  useEffect(() => { fetchNotifs(); }, [user]);
  useEffect(() => { if (newNotif) fetchNotifs(); }, [newNotif]);

  // ── Close popup on outside click ─────────────────────────
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        bellRef.current  && !bellRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  const handleMarkRead = async () => {
    try {
      await api.put("/users/notifications/read-all");
      setUnreadCount(0);
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <aside className="sidebar">

      {/* Logo */}
      <Link to="/app/dashboard" className="sidebar-logo">
        <div className="sidebar-logo-icon">G</div>
        <span className="sidebar-logo-text">GLA Exchange</span>
      </Link>

      {/* Sell CTA */}
      <Link to="/app/sell" className="sidebar-sell-btn">
        <PlusCircle size={18} />
        List an Item
      </Link>

      {/* Navigation */}
      <nav className="sidebar-nav">
        <span className="sidebar-section-label">Menu</span>

        {navLinks.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to}
            className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}>
            <Icon size={18} />
            <span>{label}</span>
            {to === "/chat" && unreadChats > 0 && (
              <span className="sidebar-badge">
                {unreadChats > 9 ? "9+" : unreadChats}
              </span>
            )}
          </NavLink>
        ))}

        {/* Admin link */}
        {isAdmin && (
          <>
            <span className="sidebar-section-label" style={{ marginTop: "var(--space-4)" }}>
              Admin
            </span>
            <NavLink to="/admin"
              className={({ isActive }) => "sidebar-link " + (isActive ? "active" : "")}>
              <Shield size={18} />
              <span>Admin Portal</span>
            </NavLink>
          </>
        )}

        {/* ── Notifications Bell ────────────────────────────── */}
        <span className="sidebar-section-label" style={{ marginTop: "var(--space-4)" }}>
          Notifications
        </span>

        <div className="sidebar-notif-wrapper">
          <button
            ref={bellRef}
            className={"sidebar-link sidebar-bell-btn " + (notifOpen ? "active" : "")}
            onClick={() => setNotifOpen((o) => !o)}
          >
            <div className="sidebar-bell-icon-wrap">
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="sidebar-bell-dot">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </div>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span className="sidebar-badge" style={{ marginLeft: "auto" }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* ── Glassmorphism Popup ───────────────────────────── */}
          {notifOpen && (
            <>
              <div className="notif-backdrop" onClick={() => setNotifOpen(false)} />
              <div ref={popupRef} className="notif-popup">
                <div className="notif-popup-header">
                  <div className="notif-popup-title">
                    <Bell size={15} />
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="notif-popup-count">{unreadCount}</span>
                    )}
                  </div>
                  <div className="notif-popup-actions">
                    {unreadCount > 0 && (
                      <button className="notif-markread-btn" onClick={handleMarkRead}>
                        <CheckCheck size={13} />
                        <span>Mark all read</span>
                      </button>
                    )}
                    <button className="notif-close-btn" onClick={() => setNotifOpen(false)}>
                      <X size={15} />
                    </button>
                  </div>
                </div>

                <div className="notif-popup-list">
                  {notifs.length === 0 ? (
                    <div className="notif-popup-empty">
                      <Bell size={28} strokeWidth={1.2} />
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    notifs.map((n, i) => (
                      <div
                        key={n._id}
                        className={"notif-popup-item " + (!n.read ? "unread" : "")}
                        style={{ animationDelay: `${i * 40}ms` }}
                      >
                        {!n.read && <span className="notif-item-dot" />}
                        <div className="notif-item-body">
                          <div className="notif-item-title">{n.title}</div>
                          <div className="notif-item-msg">{n.message}</div>
                          <div className="notif-item-time">
                            {new Date(n.createdAt).toLocaleDateString("en-IN", {
                              day: "numeric", month: "short",
                            })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          {user?.avatar
            ? <img src={user.avatar} alt={user.name} className="avatar avatar-sm" />
            : <div className="avatar-placeholder avatar-sm">{user?.name?.[0]}</div>
          }
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user?.name}</span>
            <span className="sidebar-user-roll">{user?.rollNumber}</span>
          </div>
        </div>
        <button className="sidebar-logout" onClick={logout} title="Sign out">
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}